
import { Cropper } from 'vue-advanced-cropper';
import {
  defineComponent, ref, watch, onMounted, nextTick, computed,
} from 'vue';
import { Splide, SplideSlide, Options } from '@splidejs/vue-splide';
import AppTakePhotoOptions from '@/components/AppTakePhotoOptions.vue';
import AppLabelInput from '@/components/UI/AppLabelInput.vue';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
import useFlutter from '@/composables/UseFlutter';
import 'vue-advanced-cropper/dist/style.css';
import '@splidejs/vue-splide/dist/css/splide-core.min.css';
import {
  IFileUpload, IImageCrop, ISearchLabelInput, ITagInput, ICropCoordinates,
} from '@/types/interfaces';

export default defineComponent({
  emits: ['on-done', 'on-cancel', 'on-retake'],
  components: {
    Cropper,
    AppTakePhotoOptions,
    AppLabelInput,
    Splide,
    SplideSlide,
  },
  props: {
    image: { type: Object as () => IImageCrop, required: true },
    isCaseCropper: { type: Boolean, default: false },
    hideOptionsList: { type: Boolean, default: false },
    doneBtnText: { type: String, default: 'app.take.photo.done.button' },
    doneBtnIcon: { type: String, default: 'icon-check' },
    showLabels: { type: Boolean, default: false },
    zIndex: { type: String, default: '100' },
    labels: { type: Array as () => ISearchLabelInput[], default: [] as ISearchLabelInput[] },
    singleUpload: { type: Boolean, default: false },
  },
  setup(props, context) {
    const mainStore = useMainStore();
    const croppers = ref([]);
    const showOptions = ref(false);
    const fileToUpload = ref();
    const counter = ref(0);

    const main = ref<InstanceType<typeof Splide>>();
    const thumbs = ref<InstanceType<typeof Splide>>();

    const mainOptions: Options = {
      perPage: 1,
      perMove: 1,
      arrows: false,
      pagination: false,
      type: 'fade',
      drag: false,
      direction: 'rtl',
    };

    const thumbOptions: Options = {
      width: 'auto',
      height: 50,
      arrows: false,
      pagination: false,
      cover: true,
      isNavigation: true,
      updateOnMove: true,
      perPage: 5.5,
      perMove: 1,
      gap: '4px',
      direction: 'rtl',
    };

    // Slider
    const currentSlide = ref(0);
    function handleOnChange(nI) {
      currentSlide.value = nI.index;
    }

    const images = ref([] as IImageCrop[]);

    const filesToUpload = ref([] as IFileUpload[]);
    const caseStencil = { resizable: true };
    const profileStencil = {
      aspectRatio: 1,
      previewClass: 'preview',
      movable: true,
      resizable: true,
    };

    function resetAppTakePhoto() {
      images.value = [];
      croppers.value = [];
      showOptions.value = false;
    }

    const isFlutter = computed(() => mainStore.isFlutter);
    const { flutterOpenCamera, flutterAddMedia } = useFlutter();
    // watch(() => mainStore.flutterCameraFile, (newValue, oldValue) => {
    //   console.warn('NEW_VALUE ', newValue, ' OLD_VALUE ', oldValue);
    //   if (newValue) {
    //     console.log('NEW FILE ADDED');
    //     flutterAddMedia(newValue).then((r) => {
    //       images.value.push(r);
    //       mainStore.flutterCameraFile = null;
    //       // clear mainStore.flutterCameraFile or use subscribe to store instead of component watch
    //     });
    //   }
    // });

    // final onDone - effect save & update
    async function onDone(value = {}) {
      if (Object.prototype.hasOwnProperty.call(value, 'to')) {
        context.emit('on-done', { ...value, file: filesToUpload.value });
      } else {
        context.emit('on-done', { file: filesToUpload.value });
      }
      resetAppTakePhoto();
    }

    function onCancel() {
      mainStore.inputPhotoTags = [];
      filesToUpload.value = [];
      if (props.singleUpload) {
        images.value = [];
      }
      context.emit('on-cancel');
    }

    function onOptionsClose():void {
      showOptions.value = false;
      filesToUpload.value = [];
    }

    function isCropped(img: any, coordinates: ICropCoordinates): boolean {
      const { width, height } = img.imageSize;
      return !(coordinates.width === width && coordinates.height === height);
    }

    function handleCrop() {
      filesToUpload.value = [];

      let mediaCounter = 0;

      for (let c = 0; c < croppers.value.length; c += 1) {
        if (images.value[c].fileType?.includes('video')) {
          fetch(images.value[c].url).then((data) => data.blob()).then((blob) => {
            const f = new File([blob], images.value[c].fileName, { type: images.value[c].fileType });
            filesToUpload.value.push({
              file: f, labels: (images.value[c]?.labels), cUrl: images.value[c].url, poster: images.value[c].poster,
            });
          })
            // eslint-disable-next-line no-loop-func
            .then(() => {
              if (props.hideOptionsList) {
                mediaCounter += 1;
                if (mediaCounter === croppers.value.length) {
                  onDone();
                }
              }
            });
        } else {
          const { coordinates } = (croppers!.value![c] as any).getResult();
          const {
            fileName, fileType, labels, url,
          } = images.value[c];
          const imgCoordinates = isCropped(croppers.value[c], coordinates) ? coordinates : undefined;

          fetch(url).then((data) => data.blob()).then((blob) => {
            const f = new File([blob], fileName, { type: fileType });
            filesToUpload.value.push({ file: f, labels, coodrinates: imgCoordinates });
          })
            // eslint-disable-next-line no-loop-func
            .then(() => {
              if (props.hideOptionsList) {
                mediaCounter += 1;
                if (mediaCounter === croppers.value.length) {
                  onDone();
                }
              }
            });
        }
      }

      if (!props.hideOptionsList) {
        showOptions.value = true;
      }
    }

    function rotateLeft() {
      (croppers.value[currentSlide.value] as any).rotate(-90);
    }

    function handleReTake() {
      if (images.value.length < 21) {
        context.emit('on-retake');
      }
    }

    function onDelete() {
      // croppers.value.splice(currentSlide.value, 1);
      images.value.splice(currentSlide.value, 1);
      if (images.value.length === 0) {
        handleReTake();
      }
    }

    function onDeleteFlutter() {
      // croppers.value.splice(currentSlide.value, 1);
      images.value.splice(currentSlide.value, 1);
      if (images.value.length === 0) {
        // context.emit('on-cancel');
      }
    }

    function defaultSize({ imageSize, visibleArea }) {
      return {
        width: (visibleArea || imageSize).width,
        height: (visibleArea || imageSize).height,
      };
    }

    // Photo labels
    const showAddLabel = ref(false);
    function addLabelsOpen() {
      if (images.value[currentSlide.value].labels?.length) {
        mainStore.inputPhotoTags = images.value[currentSlide.value].labels as ITagInput[];
      }
      showAddLabel.value = true;
    }
    function addLabelsClose() {
      images.value[currentSlide.value].labels = mainStore.inputPhotoTags;
      mainStore.inputPhotoTags = [];
      showAddLabel.value = false;
    }

    function cancelLabelsPopup() {
      mainStore.inputPhotoTags = [];
      showAddLabel.value = false;
    }

    function deleteLabel(label) {
      images.value.forEach((el) => {
        /* eslint-disable no-param-reassign */
        el.labels = el.labels?.filter((elLabel) => elLabel.text !== label);
      });
      filesToUpload.value.forEach((el) => {
        /* eslint-disable no-param-reassign */
        el.labels = el.labels?.filter((elLabel) => elLabel.text !== label);
      });
    }

    function handleLabelsMerge(labelsToMerge: ITagInput[]) {
      images.value.forEach((el) => {
        const existing = el.labels?.map((item) => item.text) || [];
        const newest = labelsToMerge.map((nw) => nw.text) || [];
        const newLabelsSet = [...new Set([...existing, ...newest])];
        /* eslint-disable no-param-reassign */
        el.labels = newLabelsSet.map((x) => ({ text: x }));
      });
      filesToUpload.value.forEach((el) => {
        const existing = el.labels?.map((item) => item.text) || [];
        const newest = labelsToMerge.map((nw) => nw.text) || [];
        const newLabelsSet = [...new Set([...existing, ...newest])];
        /* eslint-disable no-param-reassign */
        el.labels = newLabelsSet.map((x) => ({ text: x }));
      });
    }

    watch(() => props.image, (nv) => {
      console.log('SPA MODE', nv);
      if (nv?.url) {
        if (props.singleUpload) {
          images.value = [];
          images.value.push(props.image);
          counter.value += 1;
        } else {
          images.value.unshift(props.image);
          currentSlide.value = images.value.length - 1;
          const thumbsSplide = thumbs.value?.splide;
          if (thumbsSplide) {
            // eslint-disable-next-line no-unused-expressions
            thumbsSplide.Components.Controller.setIndex(0);
            const idx = thumbsSplide.index;
            // eslint-disable-next-line no-unused-expressions
            main.value?.sync(thumbs!.value!.splide!);
            // eslint-disable-next-line no-unused-expressions
            main.value?.splide?.Components.Controller.setIndex(0);
          }
        }
      }
    });

    watch(() => images.value.length, () => {
      // croppers.value = croppers.value.filter((item) => item !== null);
      nextTick(() => {
        croppers.value = croppers.value.filter((item) => item !== null);
      });
    });

    onMounted(() => {
      const thumbsSplide = thumbs.value?.splide;
      if (thumbsSplide) {
        // eslint-disable-next-line no-unused-expressions
        main.value?.sync(thumbsSplide);
      }
    });

    return {
      isFlutter,
      flutterOpenCamera,
      croppers,
      caseStencil,
      profileStencil,
      showOptions,
      fileToUpload,
      counter,

      showAddLabel,
      addLabelsOpen,
      addLabelsClose,
      deleteLabel,

      handleCrop,
      rotateLeft,
      onDone,
      defaultSize,
      onCancel,
      handleReTake,
      onDelete,
      onDeleteFlutter,
      onOptionsClose,

      filesToUpload,

      images,
      resetAppTakePhoto,
      // slider
      currentSlide,
      handleOnChange,

      handleLabelsMerge,
      cancelLabelsPopup,

      main,
      thumbs,
      mainOptions,
      thumbOptions,
    };
  },
});
